import React, { useEffect, useMemo, useState } from 'react'
import { BUTTON_THEME, CircularLoader, Button } from '@st-operations/uikit/src/v2/index.js'

import User from '_lib/User'
import { logErrorAsync } from '_lib/utility'

import Header from '_components/header/index.jsx'
import ContentRuleChangeRequestsStatusBadge from '_components/content-rule-change-request-status-badge'
import Table from '_components/table'
import {
  getContentRuleChangeRequest,
  getContentRule,
  updateContentRuleChangeRequest,
  updateContentRule,
  uploadS3,
} from '_services/content-rule'
import arrayDiff from '_lib/arrayDiff'

const ContentRuleChangeRequest = (props) => {
  const { id } = props.match.params

  const [originalContentRule, setOriginalContentRule] = useState()
  const [data, setData] = useState()
  const [inRequest, setInRequest] = useState(false)
  const [allowedToUpdate, setAllowedToUpdate] = useState(false)

  const differentProperties = useMemo(() => Object.keys(data?.difference || {}), [data])

  useEffect(() => {
    User.watch(this, (user) => {
      if (user) {
        setAllowedToUpdate(
          user?.datasphere_permissions?.content_rules?.permissions?.configuration
            ?.allowed_to_update_content_rules
        )
      }
    })

    const load = async (id) => {
      try {
        const data = await getContentRuleChangeRequest(id)
        setData(data)
        const originalContentRule = await getContentRule(data.contentRule._id)
        setOriginalContentRule(originalContentRule)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    load(id)
  }, [id])

  const handleSaveAs = async (status = 'reject', customRoute = false, baseObject) => {
    if (inRequest) return
    setInRequest('Updating')

    function changeStatus(status) {
      return {
        ...(baseObject || data),
        status,
      }
    }

    try {
      if (status === 'approved') {
        await updateContentRule(originalContentRule._id, {
          ...originalContentRule,
          ...data.difference,
        })

        toast('Distributing changes to s3...')
        await uploadS3('content-rules')
        toast.success('Changes distributed to s3!')
      }
      const requestedChangeStatus = changeStatus(status)
      await updateContentRuleChangeRequest(id, requestedChangeStatus)

      if (!customRoute) {
        toast.success('Update successful!')
        handleExit()
      }
      return true
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
    }
  }

  const handleExit = () => {
    props.history.push(`/content-rule-change-requests`)
  }

  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  const valueToString = (property) => {
    let text
    if (!property) {
      text = `(empty)`
    }
    if (Array.isArray(property)) {
      text = JSON.stringify(property.map((m) => m.name || m))
    } else if (typeof property === 'object') {
      text = JSON.stringify(property.name || property)
    } else if (typeof property === 'string') {
      text = `"${property}"`
    } else {
      text = `${property}`
    }
    return text
  }

  if (!originalContentRule || !data.difference) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <>
      <Header
        title={
          <div className="flex flex-row gap-4 items-center">
            <span>{originalContentRule.name}</span>
            <ContentRuleChangeRequestsStatusBadge status={data.status} />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={data.status === 'pending' && allowedToUpdate}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSaveAs('rejected')}>
            Reject
          </Button>
          <Button
            theme={BUTTON_THEME.PRIMARY}
            onClick={async () => {
              handleSaveAs('approved')
            }}
          >
            Approve
          </Button>
        </If>
      </Header>
      <Table
        columns={[
          {
            name: 'Property',
            key: 'property',
          },
          {
            name: 'New',
            key: 'newValue',
            // eslint-disable-next-line react/display-name
            render: (item) => {
              let originalValue = item.originalValue
              const originalLineBreakSplit =
                (typeof originalValue === 'string' && originalValue?.split('\n')) || []
              if (originalLineBreakSplit.length > 1) {
                originalValue = originalLineBreakSplit
              }

              let newValue = item.newValue
              const newLineBreakSplit =
                (typeof newValue === 'string' && newValue?.split('\n')) || []
              if (newLineBreakSplit.length > 1) {
                newValue = newLineBreakSplit
              }

              if (Array.isArray(newValue)) {
                const originalValueArray = Array.isArray(originalValue) ? originalValue : []
                const { added, removed } = arrayDiff(originalValueArray, newValue)
                let render = []
                if (added.length > 0) {
                  render.push(
                    <ul className="text-green-500 font-bold list-none">
                      {added.map((value) => (
                        <li key={value}>+ {valueToString(value)}</li>
                      ))}
                    </ul>
                  )
                }
                if (removed.length > 0) {
                  render.push(
                    <ul className="text-red-600 font-bold list-none">
                      {removed.map((value) => (
                        <li key={value}>- {valueToString(value)}</li>
                      ))}
                    </ul>
                  )
                }

                if (added.length > 0 || removed.length > 0) {
                  return render
                }
              }
              return valueToString(newValue)
            },
          },
        ]}
        data={differentProperties.map((property) => ({
          property,
          originalValue: originalContentRule?.[property],
          newValue: data.difference[property],
        }))}
      />
    </>
  )
}

export default ContentRuleChangeRequest
